import { useAuth } from "@/_core/hooks/useAuth";
import { SitePageShell } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { formatKRW } from "@shared/pricing";
import { CheckCircle2, Loader2, ShoppingBag } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { StepIndicator } from "./Create";

function useImageIdFromQuery() {
  const [location] = useLocation();
  return useMemo(() => {
    const url = new URL(window.location.href);
    const id = url.searchParams.get("image");
    return id ? Number(id) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
}

/** "oil" | "custom" — forwarded from Create.tsx's entryMode (see
 *  Create.tsx's goNext()). Used only to pick which V1 size products this
 *  customer may see/select; not touched at all if absent (e.g. reaching
 *  here from a plain /create with no mode). */
function useModeFromQuery() {
  const [location] = useLocation();
  return useMemo(() => {
    const m = new URL(window.location.href).searchParams.get("mode");
    return m === "oil" || m === "custom" ? m : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
}

export default function OptionsPage() {
  const { isAuthenticated, loading } = useAuth();
  const [, navigate] = useLocation();
  const imageId = useImageIdFromQuery();
  const mode = useModeFromQuery();

  const products = trpc.products.listActive.useQuery();
  const [sizeId, setSizeId] = useState<number | null>(null);
  const [frameId, setFrameId] = useState<number | null>(null);
  const [finishId, setFinishId] = useState<number | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      window.location.href = getLoginUrl();
      return;
    }
    if (!imageId) navigate("/create");
  }, [loading, isAuthenticated, imageId, navigate]);

  // V1 size products are named size_<size>_oil / size_<size>_custom so one
  // customer never sees the other product family's price. When mode is
  // absent (e.g. a bare /create with no entryMode) all "size" products are
  // shown, matching the previous behavior.
  const sizes = useMemo(() => {
    const all = (products.data ?? []).filter((p) => p.kind === "size");
    if (mode === "oil") return all.filter((p) => p.code.endsWith("_oil"));
    if (mode === "custom") return all.filter((p) => p.code.endsWith("_custom"));
    return all;
  }, [products.data, mode]);

  const selectedSizeLabel = useMemo(
    () => sizes.find((s) => s.id === sizeId)?.sizeLabel ?? null,
    [sizes, sizeId],
  );

  // Frames are per-size (frame_a4/frame_a3/frame_a2, matched via
  // products.sizeLabel — no schema change needed, the column already
  // existed). Until a size is selected there's no size to match against,
  // so show none rather than an unrelated frame's price.
  const frames = useMemo(() => {
    if (!selectedSizeLabel) return [];
    return (products.data ?? []).filter(
      (p) => p.kind === "frame" && p.sizeLabel === selectedSizeLabel,
    );
  }, [products.data, selectedSizeLabel]);

  const finishes = useMemo(
    () => (products.data ?? []).filter((p) => p.kind === "finish"),
    [products.data],
  );

  // Auto-select the first size once the (mode-filtered) list is available.
  useEffect(() => {
    if (sizeId || sizes.length === 0) return;
    setSizeId(sizes[0].id);
  }, [sizes, sizeId]);

  // Keep the frame in sync with whichever size is currently selected —
  // frames are per-size, so switching size must switch frame too, not just
  // fill it in once. Also handles the "no frame for this size" case.
  useEffect(() => {
    if (frames.length === 0) {
      if (frameId !== null) setFrameId(null);
      return;
    }
    if (!frameId || !frames.some((f) => f.id === frameId)) {
      setFrameId(frames[0].id);
    }
  }, [frames, frameId]);

  useEffect(() => {
    if (finishId || finishes.length === 0) return;
    setFinishId(finishes[0].id);
  }, [finishes, finishId]);

  const selectedIds = useMemo(
    () => [sizeId, frameId, finishId].filter((v): v is number => Boolean(v)),
    [sizeId, frameId, finishId],
  );

  const estimate = trpc.orders.estimate.useQuery(
    { productIds: selectedIds },
    { enabled: selectedIds.length > 0 },
  );

  if (loading || !isAuthenticated || !products.data) {
    return (
      <SitePageShell>
        <div className="container py-20 flex justify-center">
          <Loader2 className="animate-spin text-muted-foreground" />
        </div>
      </SitePageShell>
    );
  }

  const goCheckout = () => {
    if (!sizeId) return toast.error("캔버스 사이즈를 선택해주세요");
    const params = new URLSearchParams();
    params.set("image", String(imageId));
    params.set("size", String(sizeId));
    if (frameId) params.set("frame", String(frameId));
    if (finishId) params.set("finish", String(finishId));
    navigate(`/checkout?${params.toString()}`);
  };

  return (
    <SitePageShell>
      <div className="container py-6 md:py-10 pb-24 lg:pb-10 max-w-6xl mx-auto">
        <StepIndicator current={2} />

        <div className="mt-8 grid lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Options */}
          <div className="lg:col-span-7 space-y-6">
            <div>
              <h1 className="font-serif text-2xl md:text-3xl text-primary">
                사이즈와 옵션 선택
              </h1>
              <p className="text-muted-foreground mt-1.5 text-sm">
                작품에 가장 잘 어울리는 캔버스, 프레임, 마감을 선택해주세요.
              </p>
            </div>

            <OptionGroup
              title="캔버스 사이즈"
              items={sizes}
              selectedId={sizeId}
              onSelect={setSizeId}
            />
            <OptionGroup
              title="프레임"
              items={frames}
              selectedId={frameId}
              onSelect={setFrameId}
            />
            <OptionGroup
              title="유화 질감 옵션"
              items={finishes}
              selectedId={finishId}
              onSelect={setFinishId}
            />
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-5">
            <div className="sticky top-20 space-y-4">
              {imageId && (
                <Card className="overflow-hidden">
                  <CardContent className="p-0">
                    <SelectedImagePreview imageId={imageId} />
                  </CardContent>
                </Card>
              )}

              <Card className="border-primary/20">
                <CardContent className="p-5 space-y-4">
                  <div className="font-serif text-lg text-primary">
                    주문 요약
                  </div>
                  {estimate.data ? (
                    <div className="space-y-2 text-sm">
                      {estimate.data.products.map((p) => (
                        <div
                          key={p.id}
                          className="flex justify-between text-muted-foreground"
                        >
                          <span>{p.name}</span>
                          <span>{formatKRW(p.price)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between pt-3 border-t border-border/60">
                        <span className="text-muted-foreground">배송비</span>
                        <span className="text-green-600 font-medium">
                          {estimate.data.breakdown.shipping === 0
                            ? "무료"
                            : formatKRW(estimate.data.breakdown.shipping)}
                        </span>
                      </div>
                      <div className="flex justify-between pt-3 border-t border-border/60">
                        <span className="font-medium text-primary">총 결제금액</span>
                        <span className="font-serif text-2xl tabular-nums text-primary">
                          {formatKRW(estimate.data.breakdown.total)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-muted-foreground text-sm">
                      옵션을 선택하면 가격이 표시됩니다.
                    </div>
                  )}
                  <Button
                    size="lg"
                    className="w-full rounded-full text-base"
                    onClick={goCheckout}
                  >
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    결제하기
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Fixed Bottom */}
      <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-background/95 backdrop-blur-md border-t border-border/60 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] z-50">
        <div className="flex items-center justify-between gap-3">
          <div>
            {estimate.data && (
              <div className="font-serif text-lg tabular-nums text-primary">
                {formatKRW(estimate.data.breakdown.total)}
              </div>
            )}
          </div>
          <Button onClick={goCheckout} className="rounded-full px-6 font-semibold">
            결제하기
          </Button>
        </div>
      </div>
    </SitePageShell>
  );
}

function OptionGroup({
  title,
  items,
  selectedId,
  onSelect,
}: {
  title: string;
  items: { id: number; name: string; description: string | null; price: number }[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  return (
    <div>
      <div className="font-serif text-lg text-primary mb-3">{title}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3">
        {items.map((p) => {
          const active = selectedId === p.id;
          return (
            <button
              key={p.id}
              onClick={() => onSelect(p.id)}
              className={
                "relative text-left rounded-lg border p-3 md:p-4 transition-all " +
                (active
                  ? "border-primary bg-primary/5 ring-1 ring-primary/20 shadow-sm"
                  : "border-border/60 bg-card hover:border-primary/40")
              }
            >
              {active && (
                <CheckCircle2 className="absolute top-2 right-2 w-4 h-4 text-primary" />
              )}
              <div className="text-sm font-medium text-primary">{p.name}</div>
              {p.description && (
                <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">
                  {p.description}
                </div>
              )}
              <div className="text-xs font-medium mt-1.5 text-amber-700">
                {p.price === 0 ? "기본 포함" : `+${formatKRW(p.price)}`}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SelectedImagePreview({ imageId }: { imageId: number }) {
  const sessionId = useMemo(() => {
    return window.localStorage.getItem("ops-last-session") ?? "";
  }, []);
  const list = trpc.images.listSession.useQuery(
    { sessionId },
    { enabled: Boolean(sessionId) },
  );
  const img = list.data?.find((i) => i.id === imageId);
  if (img) {
    return (
      <img
        src={img.url}
        alt={img.prompt}
        className="aspect-[4/3] w-full object-cover"
      />
    );
  }
  return (
    <div className="aspect-[4/3] w-full bg-muted flex items-center justify-center text-xs text-muted-foreground">
      작품 #{imageId}
    </div>
  );
}
